import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/apiClient";

/**
 * Zebra GX430t 4x3-inch 300 DPI label preview + print.
 *
 * Layout targets a `4in x 3in` @media page block. Text ALWAYS wraps —
 * no truncation. Uses word-break: break-word.
 * Also records the print via `/api/cases-v2/{id}/record-print` on first render.
 */
export default function CaseV2PrintLabelPage() {
  const { case_id } = useParams();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const recordedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cases-v2/${case_id}`);
      setDetail(data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  }, [case_id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (detail && !recordedRef.current) {
      recordedRef.current = true;
      api.post(`/cases-v2/${case_id}/record-print`, { print_type: "label" }).catch(() => {});
      // Slight delay to allow layout, then trigger browser print.
      const t = setTimeout(() => window.print(), 250);
      return () => clearTimeout(t);
    }
  }, [detail, case_id]);

  if (error) return <div className="p-4 text-rose-700">{error}</div>;
  if (!detail) return <div className="p-4 text-slate-400">Loading…</div>;

  const prettyItem = (c) => {
    const base = c.item_type === "other" ? (c.custom_name || "other") : c.item_type;
    return String(base).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  };
  const contents = (detail.contents || [])
    .map((c) => `${c.quantity}× ${prettyItem(c)}`)
    .join(", ");
  const fmtStorage = (s) => {
    const t = String(s).trim();
    return /ssd|hdd|emmc|nvme/i.test(t) ? t : `${t} SSD`;
  };
  const openedAt = detail.opened_at ? new Date(detail.opened_at).toLocaleString() : "";
  const passwordSummary = detail.password_section?.windows_password || "—";
  const warrantyText =
    detail.warranty_days == null
      ? "As per contract"
      : detail.warranty_days === 0
      ? "Paid Service"
      : `${detail.warranty_days}d${detail.warranty_end_date ? ` · till ${detail.warranty_end_date}` : ""}`;

  return (
    <>
      <style>{`
        @page { size: 4in 3in; margin: 0; }
        html, body { margin: 0; padding: 0; background: #fff; }
        body { font-family: 'Helvetica', 'Arial', sans-serif; color: #000; }
        .label {
          width: 4in; height: 3in;
          padding: 0.10in 0.12in;
          box-sizing: border-box;
          font-size: 9pt;
          line-height: 1.15;
          overflow: hidden;
          word-break: break-word;
          overflow-wrap: anywhere;
          white-space: normal;
        }
        .label .case-no {
          font-size: 20pt;
          font-weight: 900;
          letter-spacing: 0.5pt;
          line-height: 1.0;
          border-bottom: 1.5pt solid #000;
          padding-bottom: 2pt;
          margin-bottom: 3pt;
        }
        .label .row { display: flex; gap: 0.08in; align-items: flex-start; }
        .label .row .k { min-width: 0.55in; font-weight: 700; font-size: 8pt; text-transform: uppercase; }
        .label .row .v { flex: 1; font-size: 9pt; word-break: break-word; overflow-wrap: anywhere; }
        .label .inline-block { padding: 2pt 0; }
        .label .footer { font-size: 7.5pt; color: #000; margin-top: 3pt; border-top: 0.5pt solid #000; padding-top: 2pt; }
        .no-print { padding: 12px 16px; }
        @media print { .no-print { display: none; } }
      `}</style>
      <div className="no-print">
        <button onClick={() => window.print()}
          className="rounded-md bg-slate-900 text-white px-3 py-1.5 text-sm">Print again</button>
        <span className="ml-3 text-sm text-slate-500">Zebra GX430t · 4×3 · 300 DPI</span>
      </div>
      <div className="label" data-testid="zebra-label-print">
        <div className="case-no" data-testid="label-case-no">{detail.case_no}</div>

        <div className="row"><div className="k">Cust</div>
          <div className="v"><b>{detail.customer_name}</b> ({detail.mobile})</div>
        </div>
        <div className="row"><div className="k">Laptop</div>
          <div className="v">
            {[detail.brand, detail.model].filter(Boolean).join(" ") || "—"}
            {detail.processor ? ` · ${detail.processor}` : ""}
            {detail.ram ? ` · ${detail.ram} RAM` : ""}
            {detail.storage ? ` · ${fmtStorage(detail.storage)}` : ""}
          </div>
        </div>
        <div className="row"><div className="k">Serial</div>
          <div className="v">{detail.manufacturer_serial || (detail.laptop_power_status === "dead" ? "N/A (dead)" : "—")}</div>
        </div>
        <div className="row"><div className="k">Warr</div>
          <div className="v">{warrantyText} · {detail.customer_source || ""}</div>
        </div>
        <div className="row"><div className="k">Pwd</div>
          <div className="v" data-testid="label-windows-password" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{passwordSummary}</div>
        </div>
        <div className="row"><div className="k">Items</div>
          <div className="v">{contents || "—"}</div>
        </div>
        <div className="row"><div className="k">Problem</div>
          <div className="v">{detail.complaint}</div>
        </div>
        <div className="footer">
          Received: {openedAt} · By {detail.created_by_label || "—"}
          {detail.has_battery_problem ? " · BATTERY" : ""}
        </div>
      </div>
    </>
  );
}
